import { AccessTime, Close } from '@mui/icons-material';
import {
  Avatar,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { paths } from 'src/paths';
import {
  getPatientIdAction,
  switchTherapistDisciplineAction,
} from 'src/redux/actions/patientAction';
import {
  AwardIcon,
  CheckIcon,
  CodeCircle,
  IconTask,
  NoteIcon,
  NoteInfoIcon,
} from '../dashboard-icon';
import RestoreSkillCard from './restore-skill-card';
import { formatPatientData, ignoreGroup } from 'src/layouts/components/utils-method';
import { resetUuidAction } from 'src/redux/actions/documentationAction';
import PatientGoalInfo from 'src/pages/PatientGoalInfo';
import { toCalendarDay } from './calendar-day';

type Props = {
  taskInfo: any;
  handleCloseDrawer: any;
  therapistUuid: any;
  selectedDate: any
};

export const DotIcon = () => {
  return (
    <svg
      width="4"
      height="4"
      viewBox="0 0 4 4"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="2"
        cy="2"
        r="2"
        fill="#D2D6DB"
      />
    </svg>
  );
};

const { patients, patientDetails, episodeOfCare, details, createDocument } = paths;

const TaskDetails = (props: Props) => {
  const { taskInfo, handleCloseDrawer, therapistUuid, selectedDate } = props;
  const getTherapistProfileData = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data?.providerId
  );
  const therapistProfileData = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const navigate = useNavigate();
  const patientInfoV2 = useSelector((state: any) => state.getDailySessionPatientInfoV2);
  const dispatch = useDispatch();
  const taskConstants: any = {
    EVALUATION: 'Evaluation',
    THERAPY: 'Therapy',
    GROUP_THERAPY: 'Group Therapy',
    INSURANCE_CALL: 'Insurance call',
    SCREENINGS: 'Screenings',
    CONCURRENT: 'Concurrent',
  };

  const statusStyleProps: any = {
    IN_PROGRESS: {
      color: '#6366F1',
      width: 'fit-content',
      padding: '0px 8px',
      backgroundColor: '#EBEEFE',
    },
    NOT_STARTED: {
      color: '#6C737F',
      backgroundColor: 'rgba(17, 25, 39, 0.08)',
      width: 'fit-content',
      padding: '0px 8px',
    },
    FINALIZED: {
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
      width: 'fit-content',
      padding: '0px 8px',
    },
    SIGNED: {
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
      width: 'fit-content',
      padding: '0px 8px',
    },
  };

  const isPatientTaskEvaluation =
    ((patientInfoV2?.response?.codes?.length === 0 || patientInfoV2?.response?.dischargeDate) &&
    patientInfoV2?.response?.taskInfo?.length === 1 &&
      patientInfoV2.response?.taskInfo[0]?.assignedTask === 'EVALUATION') || (taskInfo?.therapyTypes?.length &&
      taskInfo?.therapyTypes?.every((t:any) => t === "EVALUATION"));

  const goToEoc = () => {
    dispatch(getPatientIdAction(patientInfoV2?.response?.patientId));
    dispatch(switchTherapistDisciplineAction(''));
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: patientInfoV2?.response?.eocId,
        getPatientId: patientInfoV2?.response?.patientId,
      },
    });
  };

  const reDirectToDailySession = () => {
    if (isPatientTaskEvaluation) {
      goToEoc();
    } else {
      const selectedPatientData = {
        name: taskInfo?.name,
        uuid: taskInfo?.uuid,
        firstName: taskInfo?.firstName,
        lastName: taskInfo?.lastName,
        notesId: taskInfo?.dailySessionNoteUuid,
      };
      dispatch(resetUuidAction());
      // THIN-8018 (IS-24398): hand the DSN page the calendar day the dashboard is showing, as-is.
      // The old `valueOf() + getTimezoneOffset()` shift is the idiom for turning a bare YYYY-MM-DD
      // string (parsed as UTC midnight) back into the local day; applied to a Date that carries the
      // current time of day it pushes the clock forward by the UTC offset, so after 7pm Central /
      // 8pm Eastern the note was opened, created and billed for tomorrow.
      const dateOfService = toCalendarDay(selectedDate);
      navigate('/daily-session-notes', {
        state: {
          document: {
            dateOfService: dateOfService,
            source: 'FROM_EOC',
            dailySessionNoteUuid: taskInfo?.dailySessionNoteUuid,
            patientEoc: selectedPatientData,
          },
        },
      });
    }
  };

  return (
    <>
      {patientInfoV2?.loading ? (
        <Stack
          gap="1.5rem"
          p={3}
        >
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
            <Skeleton
              animation={false}
              variant="circular"
            >
              <Avatar sx={{ width: 70, height: 70 }} />
            </Skeleton>
            <Box sx={{ width: '100%', mt: 1 }}>
              <Typography
                width={'40%'}
                variant="h5"
              >
                <Skeleton animation={false} />
              </Typography>
              <Typography
                width={'30%'}
                variant="h5"
              >
                <Skeleton animation={false} />
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography
              marginTop={2}
              width={'100%'}
              variant="h4"
            >
              <Skeleton animation={'wave'} />
            </Typography>
            <Typography
              marginTop={1}
              width={'100%'}
              variant="h4"
            >
              <Skeleton animation={'wave'} />
            </Typography>
          </Box>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              width={'30%'}
              variant="h1"
            >
              <Skeleton animation={false} />
            </Typography>
            <Typography
              width={'30%'}
              variant="h1"
            >
              <Skeleton animation={false} />
            </Typography>
          </Box>
          {['1', '2', '3'].map((sle) => {
            return (
              <Box
                sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                key={sle}
              >
                <Typography
                  width={'20%'}
                  variant="h1"
                >
                  <Skeleton animation={false} />
                </Typography>
                <Typography
                  width={'75%'}
                  variant="h1"
                >
                  <Skeleton animation={false} />
                </Typography>
              </Box>
            );
          })}
          <Typography
            marginTop={1}
            width={'100%'}
            variant="h1"
          >
            <Skeleton animation={'wave'} />
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '90%',
            }}
          >
            <Typography
              width={'100%'}
              variant="h1"
            >
              <Skeleton animation={'wave'} />
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 1,
            px: 1,
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
          }}
        >
          <Box>
            <Stack
              direction={'column'}
              sx={{ display: 'flex' }}
              mb={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                p={2}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Avatar
                      src={`data:image/png;base64,${taskInfo?.profileImage}`}
                      sx={{
                        height: 55,
                        width: 55,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <Typography
                      sx={{ width: 200 }}
                      noWrap
                      variant="h5"
                    >
                      {`${taskInfo?.lastName} ${taskInfo?.firstName}` || '-'}
                    </Typography>
                    {/* <Chip
                      label={<Typography variant="overline">In Progress</Typography>}
                      variant="outlined"
                      size="small"
                      sx={{ color: '#6366F1', width: 120, backgroundColor: '#EBEEFE' }}
                    /> */}

                    {taskInfo?.status !== 'SIGNED' ? (
                      <Chip
                        label={
                          <Typography variant="overline">
                            {taskInfo?.status
                              ? taskInfo?.status
                                  ?.split('_')
                                  ?.map(
                                    (item: string) =>
                                      item.slice(0, 1) + item.toLowerCase().slice(1, item.length)
                                  )
                                  .join(' ')
                              : 'NOT STARTED'}
                          </Typography>
                        }
                        variant="outlined"
                        size="small"
                        sx={statusStyleProps[taskInfo?.status ? taskInfo?.status : 'NOT_STARTED']}
                      />
                    ) : (
                      <Chip
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CheckIcon />
                            <Typography variant="overline">Signed</Typography>
                          </Box>
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          color: '#107569',
                          backgroundColor: 'rgba(21, 183, 158, 0.12)',
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box>
                  <IconButton
                    onClick={handleCloseDrawer}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </Stack>
            <Divider />
            <Stack
              divider={<Divider orientation="horizontal" />}
              sx={{
                borderColor: 'divider',
                borderRadius: 1,
                py: 2,
                gap: 1,
                px: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  sx={{ color: '#4338CA' }}
                  variant="overline"
                >
                  diagnosis
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#111927' }}
                >
                  {patientInfoV2?.response?.diagnosis?.length
                    ? patientInfoV2?.response?.diagnosis?.join(', ')
                    : '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="overline"
                  sx={{ color: '#B5BCC4' }}
                >
                  Bed Assignment
                </Typography>
                <Typography
                  sx={{ marginLeft: 1 }}
                  variant="body2"
                >
                  {formatPatientData(
                    patientInfoV2?.response?.floorNumber,
                    patientInfoV2?.response?.roomNumber,
                    patientInfoV2?.response?.bedAssignment
                  )}
                </Typography>
                <Typography
                  variant="overline"
                  sx={{ color: '#B5BCC4', marginLeft: 2 }}
                >
                  Payer
                </Typography>
                <Typography
                  sx={{ marginLeft: 1 }}
                  variant="body2"
                >
                  {patientInfoV2?.response?.payer || '-'}
                </Typography>
              </Box>
              <Box>
                <Grid
                  container
                  xs={12}
                  direction={'column'}
                >
                  {patientInfoV2?.response?.taskInfo?.map((task: any) => (
                    <>
                      <Grid
                        xs={12}
                        key={task?.assignedTask}
                        container
                        mt={1}
                        mb={2}
                      >
                        <Grid
                          sx={{ display: 'flex', gap: 2 }}
                          xs={6}
                        >
                          <div
                            style={{
                              padding: '10px',
                              backgroundColor: '#F5F7FF',
                              borderRadius: '12px',
                              height: '45px',
                            }}
                          >
                            <IconTask iconType={task?.assignedTask} />
                          </div>
                          <Stack
                            justifyContent="center"
                            alignItems="start"
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography
                                sx={{ color: '#6C737F' }}
                                variant="caption"
                              >
                                {taskConstants[task?.assignedTask]}
                              </Typography>
                            </Box>
                            {task?.groupName !== 'N/A' ? (
                              task?.groupName ? (
                                <Chip
                                  key={task?.groupName}
                                  label={
                                    <Typography variant="overline">{task?.groupName}</Typography>
                                  }
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    color: '#06AED4',
                                    backgroundColor: '#ECFDFF',
                                    m: 0.5,
                                    marginLeft: '0',
                                  }}
                                />
                              ) : null
                            ) : (
                              <>
                                {task?.groupName === 'N/A' && (
                                  <Typography
                                    sx={{ color: '#6C737F' }}
                                    variant="caption"
                                  >
                                    Individual therapy session
                                  </Typography>
                                )}
                              </>
                            )}
                            {/* */}
                          </Stack>
                        </Grid>
                        <Grid
                          sx={{ display: 'flex', gap: 2 }}
                          xs={6}
                        >
                          <div
                            style={{
                              padding: '10px',
                              backgroundColor: '#F5F7FF',
                              borderRadius: '12px',
                              height: '45px',
                            }}
                          >
                            <AccessTime />
                          </div>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography
                              sx={{ color: '#6C737F' }}
                              variant="caption"
                            >
                              Duration
                            </Typography>
                            <Typography variant="body2">{task?.time ? task?.time : '-'}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      <Divider />
                      <Grid sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
                        <div
                          style={{
                            padding: '10px',
                            backgroundColor: '#F5F7FF',
                            borderRadius: '12px',
                            height: '45px',
                          }}
                        >
                          <NoteInfoIcon />
                        </div>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography
                            sx={{ color: '#6C737F' }}
                            variant="caption"
                          >
                            Note from DOR
                          </Typography>
                          <Typography variant="body2">{task?.note ? task?.note : 'N/A'}</Typography>

                          <Typography
                            sx={{ color: '#6C737F' }}
                            variant="caption"
                          >
                            Note from previous therapy session
                          </Typography>
                          <Typography variant="body2">
                            {patientInfoV2?.response?.noteFromLastSession || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  ))}
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {' '}
                <div
                  style={{
                    padding: '10px',
                    backgroundColor: '#F5F7FF',
                    borderRadius: '12px',
                    height: '45px',
                  }}
                >
                  <AwardIcon />
                </div>
                <Box>
                  <Typography
                    sx={{ color: '#6C737F' }}
                    variant="caption"
                  >
                    Goals
                  </Typography>
                  <Stack mt={1}>
                    {therapistProfileData?.discipline === 'PT' ? (
                      <>
                        <PatientGoalInfo
                          discipline="PT"
                          isShortTerm={true}
                          goals={patientInfoV2?.response?.shortTermGoals}
                        />
                        <PatientGoalInfo
                          discipline="PT"
                          isShortTerm={false}
                          goals={patientInfoV2?.response?.longTermGoals}
                        />
                      </>
                    ) : therapistProfileData?.discipline === 'OT' ? (
                      <>
                        {' '}
                        <PatientGoalInfo
                          discipline="OT"
                          isShortTerm={true}
                          goals={patientInfoV2?.response?.shortTermGoals}
                        />
                        <PatientGoalInfo
                          discipline="OT"
                          isShortTerm={false}
                          goals={patientInfoV2?.response?.longTermGoals}
                        />
                      </>
                    ) : therapistProfileData?.discipline === 'SLP' ? (
                      <>
                        {' '}
                        <PatientGoalInfo
                          discipline="SLP"
                          isShortTerm={true}
                          goals={patientInfoV2?.response?.shortTermGoals}
                        />
                        <PatientGoalInfo
                          discipline="SLP"
                          isShortTerm={false}
                          goals={patientInfoV2?.response?.longTermGoals}
                        />
                      </>
                    ) : null}
                  </Stack>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <div
                  style={{
                    padding: '10px',
                    backgroundColor: '#F5F7FF',
                    borderRadius: '12px',
                    height: '40px',
                  }}
                >
                  <CodeCircle />
                </div>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography
                    sx={{ color: '#6C737F' }}
                    variant="caption"
                  >
                    Codes
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {Object.entries(patientInfoV2?.response?.cptCodeDescription || {})?.map(
                      ([code, description]: any, index: any) => (
                        <Chip
                          variant="outlined"
                          key={index}
                          label={
                            <Typography variant="boldText">
                              {code} | {description}
                            </Typography>
                          }
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Box>
            </Stack>

            <RestoreSkillCard
              skillData={patientInfoV2?.response?.skills}
              patientUuid={taskInfo?.uuid}
            />
          </Box>

          {therapistUuid === getTherapistProfileData ? (
            <Stack sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  isPatientTaskEvaluation &&
                  !patientInfoV2?.response?.eocId &&
                  'Episode of care is not present'
                }
              >
                <Button
                  sx={{
                    borderRadius: '12px',
                    background: '#384250',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      background: '#384250',
                    },
                  }}
                  endIcon={
                    <SvgIcon>
                      <NoteIcon />
                    </SvgIcon>
                  }
                  onClick={reDirectToDailySession}
                  variant="contained"
                  disabled={isPatientTaskEvaluation && !patientInfoV2?.response?.eocId}
                >
                  {isPatientTaskEvaluation ? 'Go to episode of care' : 'Go to session notes'}
                </Button>
              </Tooltip>
            </Stack>
          ) : null}
        </Box>
      )}
    </>
  );
};

export default TaskDetails;
